import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';

import { fetchSetlist, packSetlist } from './setlistAPI';
import initialState from './initialState';
import initialSessionState from '../session/initialState';

import { unpackSession, unpackSetlist, packSession } from "../session/sessionAPI";
import { removeAssetFromCache } from '../../services/assetStore';
import {createSetlistZip} from '../../factories/Zipper';
import { triggerBlobDownload } from '../../factories/ui';
import {addNotificationAsync} from '../ui/uiSlice';

export const unpackSessionAsync = createAsyncThunk("session/unpack", async (payload, thunkAPI) => {
  const sessionObject = await unpackSession(payload);

  return sessionObject;
});

export const unpackSetlistAsync = createAsyncThunk("session/unpackSetlist", async (payload, thunkAPI) => {
  const setlistObject = await unpackSetlist(payload);



  const setlistSessions = [];

  for (const setlistKey of Object.getOwnPropertyNames(setlistObject)) {
    const sessionBlob = setlistObject[setlistKey];
    const sessionObject = await unpackSession(sessionBlob);
    setlistSessions.push(sessionObject);
  }

  return setlistSessions;
});

export const packSetlistAsync = createAsyncThunk("session/packSetlist", async (payload, thunkAPI) => {
  
  // get setlist session from state
  // filter out all containing userAudio stems
  const sessions = thunkAPI.getState().setlist.sessions.filter(s => s.userStems.length === 0);

  // add all session zips into one master .setlist zip file
  const setListBlob = await packSetlist(sessions);

   // prompt download in browser 👍
  const dateNow = new Date();
  const setlistFilename = `mySetlist-${dateNow.toLocaleDateString().replaceAll('/','')}_${dateNow.toLocaleTimeString().replaceAll(':','')}.setlist`;
  triggerBlobDownload(setListBlob, setlistFilename);


  thunkAPI.dispatch(addNotificationAsync({
    title: 'Setlist saved',
    text: `${setlistFilename} was downloaded`,
    type: 'positive',
}))
  return true;
});

export const removeSessionFromSetlistAsync = createAsyncThunk("setlsit/remove", async (payload, thunkAPI) => {
  // remove associated items from cache
  const session = thunkAPI.getState().setlist.sessions.find(s =>s.id == payload);
  for (const userStem of session.userStems) {
    await removeAssetFromCache(userStem.id);
  }
  //remove associated song zip
  await removeAssetFromCache(session.song.pathzip);

  return payload;
});

export const setlistSlice = createSlice({
  name: 'setlist',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    createEmptySession: (state, action) => {
      const newSession = {
        ...initialSessionState,
        id: uuidv4().slice(0,8),
        title: action.payload.title,
        createdAt: new Date().toISOString(),
        song: {
          mix: action.payload.title,
          artist: '',
          len: 360,
          stems: [],
          userStems: []
        }
      };
      state.sessions.push(newSession);
    },
    createSessionFromSong: (state, action) => {
      const newSession = {
        ...initialSessionState,
        id: uuidv4().slice(0,8),
        title: action.payload.title,
        song: action.payload.song,
        createdAt: new Date().toISOString(),
        lastUpdatedAt:new Date().toISOString()
      };
      state.lastCreatedSession = newSession.id;
      state.sessions.push(newSession);
    },

    resetLastCreated: (state) => {
      state.lastCreatedSession = null;
    },
    addSongToMySetlist: (state, action) => {
      state.sessions.push(action.payload);
    },

    saveExistingSession: (state, action) => {
      state.sessions = state.sessions.filter(s=> s.id !== action.payload.id);
      state.sessions = [...state.sessions, {
        ...action.payload, 
        lastUpdatedAt:new Date().toISOString()
      }];
    }
    // // Use the PayloadAction type to declare the contents of `action.payload`
    // incrementByAmount: (state, action) => {
    //   state.value += action.payload;
    // },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(removeSessionFromSetlistAsync.fulfilled, (state, action) => {
        state.sessions = state.sessions.filter(s => s.id !== action.payload);
      })
      .addCase(packSetlistAsync.pending, (state, action) => {
   
      })
      .addCase(packSetlistAsync.rejected, (state, action) => {
        state.status = "error";
        console.error(action);
      })
      .addCase(packSetlistAsync.fulfilled, (state, action) => {
      })
      .addCase(unpackSessionAsync.pending, (state, action) => {
        state.status = "unpacking";
        state.loadingProgress = 7;
      })
      .addCase(unpackSessionAsync.rejected, (state, action) => {
        state.status = "error";
        console.error(action);
      })
      .addCase(unpackSessionAsync.fulfilled, (state, action) => {
        Object.getOwnPropertyNames(action.payload).forEach(propertyName => {
          state[propertyName] = action.payload[propertyName];
        });
        const newSession = {
          ...action.payload,
          id: uuidv4().slice(0,8),
          imported: true,
          importedAt: new Date().toISOString()
        };
        state.sessions.push(newSession);
        state.status = "ready";
        state.loadingProgress = 10;
      })
      .addCase(unpackSetlistAsync.fulfilled, (state, action) => {

        const sessionPack = [];

        for (const session of action.payload) {
          const importedSession = {};
          Object.getOwnPropertyNames(session).forEach(propertyName => {
            importedSession[propertyName] = session[propertyName];
          });
          const newSession = {
            ...importedSession,
            id: uuidv4().slice(0,8),
            imported: true,
            importedAt: new Date().toISOString()
          };
          sessionPack.push(newSession);
        } 
        state.sessions = [...state.sessions, ...sessionPack];
      })
      
      ;
  },
});

export const { addSongToMySetlist, createEmptySession, createSessionFromSong, saveExistingSession, resetLastCreated } = setlistSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
export const selectSetlistSessions = (state) => state.setlist.sessions;
export const selectLastCreatedSession = (state) => state.setlist.lastCreatedSession;
// // We can also write thunks by hand, which may contain both sync and async logic.
// // Here's an example of conditionally dispatching actions based on current state.
// export const incrementIfOdd = (amount) => (dispatch, getState) => {
//   const currentValue = selectCount(getState());
//   if (currentValue % 2 === 1) {
//     dispatch(incrementByAmount(amount));
//   }
// };

export default setlistSlice.reducer;
